/**
 * In-App message analytics (FEAT-INAPP-ANALYTICS) — per-message report opened from
 * the In-App message list. Reads RRS GET /in-app-messages/:id/stats (which merges the
 * Event in-app report with the Mongo response distributions). Chakra + the shared
 * Analytics Highcharts wrappers; data goes through RRS (x-api-key + x-access-token),
 * never x-event-key. Never renders a raw `input` value — the API returns counts only.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Heading, Tag, Button, HStack, SimpleGrid, Divider,
} from '@chakra-ui/react';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../../components/Header';
import BoxWrapper from '../../../components/BoxWrapper/BoxWrapper';
import { usePermissionRouteContext } from '../../../components/PermissionRoute';
import { hasPermission } from '../../../utils/DefaultFunction';
import { PRIMARY_BG } from '../../../theme/brand';
import {
  StatCard, StatGrid, Panel, BarChart, BreakdownTable, EmptyState, ReportFrame,
  DateRangeBar, fmtInt, fmtPct,
} from '../../Analytics/components';

const API_URL = `${process.env.REACT_APP_API_URL}/in-app-messages`;
const getHeaders = () => ({
  'x-api-key': localStorage.getItem('api-key'),
  'x-access-token': localStorage.getItem('customer-portal'),
});

const STATUS_META = {
  draft: { label: 'Draft', bg: '#F4F4F5', fg: '#71717A' },
  active: { label: 'Active', bg: '#DCFCE7', fg: '#166534' },
  paused: { label: 'Paused', bg: '#FFEDD5', fg: '#9A3412' },
  archived: { label: 'Archived', bg: '#E4E4E7', fg: '#52525B' },
};

// A distribution object { key: count } → [{label, value}] sorted desc.
const distToRows = (dist) =>
  Object.entries(dist || {})
    .map(([label, value]) => ({ label: String(label), value: Number(value || 0) }))
    .sort((a, b) => b.value - a.value);

// P-7: the export's date window. Custom → as picked; preset "last_N_days" → today − N … today;
// anything else → the last 30 days (the server default).
const ymd = (d) => d.toISOString().slice(0, 10);
export function exportDates(range, now = new Date()) {
  if (range?.mode === 'custom' && range.from && range.to) return { from: range.from, to: range.to };
  const m = /^last_(\d+)_days$/.exec(range?.range || '');
  const days = Math.min(366, m ? Number(m[1]) : 30);
  const from = new Date(now.getTime() - days * 86400000);
  return { from: ymd(from), to: ymd(now) };
}

// One response-capture block → a titled card with the right chart for its type.
function ResponseBlockCard({ block }) {
  const title = block.label || block.block_id;
  if (block.block_type === 'rating') {
    const rows = distToRows(block.distribution);
    if (block.rating_type === 'nps' && block.nps) {
      const { promoters, passives, detractors, score } = block.nps;
      return (
        <Panel title={`${title} · NPS`}>
          <Flex align="baseline" gap={2} mb={3}>
            <Text fontSize="34px" fontWeight="800" lineHeight="1" color={score >= 0 ? 'green.600' : 'red.500'} sx={{ fontVariantNumeric: 'tabular-nums' }}>{score}</Text>
            <Text fontSize="13px" color="gray.400">NPS · {fmtInt(block.count)} responses · avg {block.average}</Text>
          </Flex>
          <BarChart rows={[
            { label: 'Promoters (9–10)', value: promoters },
            { label: 'Passives (7–8)', value: passives },
            { label: 'Detractors (0–6)', value: detractors },
          ]} height={150} />
        </Panel>
      );
    }
    return (
      <Panel title={`${title} · Rating`}>
        <Text fontSize="13px" color="gray.400" mb={2}>{fmtInt(block.count)} responses · avg {block.average}</Text>
        {rows.length ? <BarChart rows={rows} height={Math.max(120, rows.length * 34)} /> : <EmptyState minimal />}
      </Panel>
    );
  }
  if (block.block_type === 'choice') {
    const rows = distToRows(block.distribution);
    return (
      <Panel title={`${title} · Choice`}>
        <Text fontSize="13px" color="gray.400" mb={2}>{fmtInt(block.count)} responses</Text>
        {rows.length ? <BreakdownTable rows={rows} labelHeader="Option" valueHeader="Responses" limit={12} /> : <EmptyState minimal />}
      </Panel>
    );
  }
  if (block.block_type === 'consent') {
    return (
      <Panel title={`${title} · Consent`}>
        <StatGrid columns={{ base: 2 }}>
          <StatCard label="Opted in" value={fmtInt(block.opted_in)} />
          <StatCard label="Opted out" value={fmtInt(block.opted_out)} />
        </StatGrid>
      </Panel>
    );
  }
  // input (and anything else). P-7 (DA-BUG-1136): RRS returns `values` only to roles with
  // in-app-view-responses; otherwise `masked`, and fields marked `sensitive` (email, phone) stay masked.
  const values = Array.isArray(block.values) ? block.values : null;
  return (
    <Panel title={`${title} · Input`}>
      <StatCard label="Submissions" value={fmtInt(block.count)}
        help={block.sensitive ? "Sensitive field (email / phone) — values stay hidden." : values ? undefined : "Individual responses aren't shown (personal data)."} />
      {values && !block.sensitive && (
        <Box mt={3} maxH="220px" overflowY="auto" borderWidth="1px" borderRadius="8px">
          {values.length ? values.map((v, i) => {
            const text = v && typeof v === 'object' ? v.value : v;
            const ts = v && typeof v === 'object' ? (v.received_at || v.at) : null;
            const at = ts ? new Date(ts).toLocaleString() : null;
            return (
              <Flex key={i} px={3} py={1.5} justify="space-between" gap={3} borderTopWidth={i ? '1px' : 0}>
                <Text fontSize="13px" color="gray.800">{String(text ?? '')}</Text>
                {at && <Text fontSize="11px" color="gray.400" whiteSpace="nowrap">{at}</Text>}
              </Flex>
            );
          }) : <Text fontSize="13px" color="gray.400" p={3}>No responses yet</Text>}
        </Box>
      )}
    </Panel>
  );
}

export default function InAppAnalytics() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [range, setRange] = useState({ mode: 'preset', range: 'last_30_days', from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (range.mode === 'custom' && range.from && range.to) { params.set('from', range.from); params.set('to', range.to); }
      else params.set('range', range.range);
      const { data: res } = await axios.get(`${API_URL}/${id}/stats?${params.toString()}`, { headers: getHeaders() });
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [id, range]);

  useEffect(() => { load(); }, [load]);

  const msg = data?.message || {};
  const f = data?.funnel || {};
  const responses = data?.responses || [];
  // P-7: clicks labelled "<Block type>: <label>", the id in the tooltip. The backend already sends the
  // label with its type ("Button: Shop now"), so it is shown as-is (V5-9: it read "Button: Button: …").
  const typeName = (t) => (t ? String(t).charAt(0).toUpperCase() + String(t).slice(1).replace(/-/g, ' ') : 'Block');
  const byBlock = (data?.by_block || []).map((r) => ({
    label: r.label || (r.block_type ? typeName(r.block_type) : r.block_id),
    title: r.block_id, value: Number(r.clicks || 0),
  }));
  // P-7: CSV export (one row per submission) for roles with in_app_view_responses.
  const routePerms = usePermissionRouteContext();
  const canViewResponses = routePerms ? hasPermission('in-app-view-responses', routePerms.permissions) : responses.some((r) => Array.isArray(r.values));
  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      // The export takes from/to dates (YYYY-MM-DD, ≤ 366 days); a preset is turned into dates.
      const params = new URLSearchParams();
      const { from, to } = exportDates(range);
      params.set('from', from); params.set('to', to);
      const { data: blob } = await axios.get(`${API_URL}/${id}/responses/export?${params.toString()}`, { headers: getHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `in-app-${id}-responses.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.status === 403 ? "You don't have permission to export responses." : 'Export failed — try again.');
    } finally { setExporting(false); }
  };
  // P-3: RRS reports dismisses apart from clicks (`funnel.dismisses`). When it does, a dismiss is
  // shown as its own figure and never as an action click; an older backend → no Dismissed figure.
  const hasDismisses = f.dismisses != null;
  const byAction = (data?.by_action || [])
    .filter((r) => !(hasDismisses && r.action === 'dismiss'))
    .map((r) => ({ label: r.action, value: Number(r.clicks || 0) }));
  const pageFunnel = (data?.page_funnel || []).map((r) => ({ label: `Page ${Number(r.page_index) + 1}`, value: Number(r.unique_viewers || 0) }));
  const hasData = Number(f.impressions || 0) > 0;
  const status = STATUS_META[msg.status] || STATUS_META.draft;

  return (
    <BoxWrapper>
      <Header />
      <Box px={{ base: 4, md: 6 }} py={4}>
        <Flex align="center" justify="space-between" mb={1} flexWrap="wrap" gap={2}>
          <HStack spacing={3}>
            <Button leftIcon={<ArrowLeft size={15} />} size="sm" variant="ghost" onClick={() => navigate('/tools/in-app-messages')}>
              Messages
            </Button>
            <Heading size="md" color="gray.800">{msg.name || 'In-App analytics'}</Heading>
            <Tag size="sm" bg={status.bg} color={status.fg} borderRadius="full">{status.label}</Tag>
          </HStack>
          <HStack spacing={2}>
            {canViewResponses && <Button size="sm" variant="outline" onClick={exportCsv} isLoading={exporting}>Export CSV</Button>}
            <DateRangeBar value={range} onChange={setRange} />
          </HStack>
        </Flex>
        <Text fontSize="13px" color="gray.400" mb={4}>
          How this message performed{msg.goal?.event ? <> · goal <b>{msg.goal.event}</b></> : null}
        </Text>

        <ReportFrame loading={loading} error={error}>
          {!hasData ? (
            <EmptyState label="No data yet — this message hasn't been shown in the selected range." />
          ) : (
            <Box>
              {/* Headline funnel */}
              <StatGrid columns={{ base: 2, md: hasDismisses ? 5 : 4 }}>
                <StatCard label="Impressions" value={fmtInt(f.impressions)} help={`${fmtInt(f.unique_viewers)} unique viewers`} />
                <StatCard label="Click-through rate" value={fmtPct(f.ctr)} help={`${fmtInt(f.unique_clickers)} clickers`} />
                <StatCard label="Submit rate" value={fmtPct(f.submit_rate)} help={`${fmtInt(f.submits)} submits`} />
                <StatCard
                  label="Conversion rate"
                  value={f.conversion_rate == null ? '—' : fmtPct(f.conversion_rate)}
                  help={f.conversion_rate == null ? 'No goal set' : `${fmtInt(f.conversions)} conversions`}
                />
                {hasDismisses && (
                  <StatCard label="Dismissed" value={fmtInt(f.dismisses)}
                    help={f.dismiss_rate == null ? 'Not counted as clicks' : `${fmtPct(f.dismiss_rate)} of impressions · not counted as clicks`} />
                )}
              </StatGrid>

              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} mt={3}>
                {/* Engagement funnel */}
                <Panel title="Engagement funnel (unique users)">
                  <BarChart rows={[
                    { label: 'Shown', value: Number(f.unique_viewers || 0) },
                    { label: 'Clicked', value: Number(f.unique_clickers || 0) },
                    { label: 'Submitted', value: Number(f.unique_submitters || 0) },
                    ...(f.conversion_rate == null ? [] : [{ label: 'Converted', value: Number(f.conversions || 0) }]),
                  ]} height={200} />
                  {hasDismisses && (
                    <Text fontSize="11px" color="gray.400" mt={2}>
                      Dismiss rate {fmtPct(f.dismiss_rate)} · {fmtInt(f.dismisses)} dismisses
                    </Text>
                  )}
                </Panel>

                {/* Page drop-off (multi-page only) */}
                {pageFunnel.length > 1 ? (
                  <Panel title="Page drop-off (unique viewers)">
                    <BarChart rows={pageFunnel} height={200} color="#7c3aed" />
                  </Panel>
                ) : (
                  <Panel title="Clicks by action">
                    {byAction.length ? <BreakdownTable rows={byAction} labelHeader="Action" valueHeader="Clicks" /> : <EmptyState minimal label="No action clicks yet" />}
                  </Panel>
                )}
              </SimpleGrid>

              {/* Block-level clicks + (when page funnel took the slot) actions */}
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3} mt={3}>
                <Panel title="Clicks by block">
                  {byBlock.length ? <BreakdownTable rows={byBlock} labelHeader="Block" valueHeader="Clicks" limit={10} /> : <EmptyState minimal label="No block clicks yet" />}
                </Panel>
                {pageFunnel.length > 1 && (
                  <Panel title="Clicks by action">
                    {byAction.length ? <BreakdownTable rows={byAction} labelHeader="Action" valueHeader="Clicks" /> : <EmptyState minimal label="No action clicks yet" />}
                  </Panel>
                )}
              </SimpleGrid>

              {/* Captured responses */}
              {responses.length > 0 && (
                <Box mt={6}>
                  <Flex align="center" gap={3} mb={3}>
                    <Heading size="sm" color="gray.700">Responses</Heading>
                    <Divider flex="1" />
                  </Flex>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
                    {responses.map((b) => <ResponseBlockCard key={b.block_id} block={b} />)}
                  </SimpleGrid>
                </Box>
              )}
            </Box>
          )}
        </ReportFrame>
      </Box>
    </BoxWrapper>
  );
}
